'use client'
import {
  getCurrentAccessToken,
  getUserData,
} from '@/app/(main)/agency/(auth)/helper'
import { getUserFromLocalStorage } from '@/utils/storage'
import { getRegionBasedBaseUrl, getRegionBasedNestApiUrl } from '@/utils/urls'
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getUserDataRegion } from './auth-service'

import { ENV } from '@/config/env'

const apiClients: Record<string, AxiosInstance> = {}

const nestApiClients: Record<string, AxiosInstance> = {}

const noAuthEndpoints = [
  '/auth/login',
  '/auth/sign-up',
  '/auth/check-email',
  '/auth/initial-signup',
  '/auth/verify-otp',
  '/auth/complete-signup',
  '/auth/complete-signup-debug',
  '/auth/resend-otp',
  '/calcom/slots',
  '/calcom/timezones'
]

const multipartRoutes = [
  '/auth/sign-up',
  '/auth/initial-signup',
  '/auth/complete-signup'
]

let failedQueue: {
  resolve: (value?: unknown) => void
  reject: (error: unknown) => void
}[] = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })

  failedQueue = []
}

const user = getUserData()
const getAxiosApiClient = async (
  userRegion: string
): Promise<AxiosInstance> => {
  console.log('Getting axios client for region', userRegion)
  if (apiClients[userRegion]) return apiClients[userRegion]

  const baseUrl = getRegionBasedBaseUrl(userRegion)

  const apiClient = axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': true
    },
    timeout: 120000,
    withCredentials: true
  })

  apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (multipartRoutes.some(route => config.url?.includes(route))) {
        config.headers['Content-Type'] = 'multipart/form-data'
      } else if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json'
      }

      if (!noAuthEndpoints.some(endpoint => config.url?.includes(endpoint))) {
        // Try to get the real Supabase token first
        const token = await getCurrentAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }

      return config
    },
    error => Promise.reject(error)
  )

  apiClient.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config

      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true

        await new Promise(resolve => setTimeout(resolve, 3000))

        try {
          return await apiClient(originalRequest)
        } catch (retryError: unknown) {
          if (axios.isAxiosError(retryError) && retryError.response?.status === 403) { 
            console.error("User Sign-out: API request sent 403 error twice in fast api instance", user?.email)
            window.location.href = `/logout?redirectTo=/agency/signin`
          }
          return Promise.reject(retryError)
        }
      }

      return Promise.reject(error)
    }
  )
  apiClients[userRegion] = apiClient
  return apiClient
}

export const getAxiosApiClientByUser = async (
  emailOverride?: string,
  regionOverride?: string
): Promise<AxiosInstance> => {
  if (regionOverride) {
    return await getAxiosApiClient(regionOverride)
  }

  if (!emailOverride) {
    const user = getUserFromLocalStorage()
    emailOverride = user?.userInfo.user.email
  }

  const userRegion = await getUserDataRegion(emailOverride ?? '')
  const apiClient = await getAxiosApiClient(userRegion)
  return apiClient
}

const getNestApiClient = async (userRegion: string): Promise<AxiosInstance> => {
  if (nestApiClients[userRegion]) return nestApiClients[userRegion]

  const baseUrl = getRegionBasedNestApiUrl(userRegion)

  const apiClient = axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': true
    },
    timeout: 120000,
    withCredentials: true
  })

  apiClient.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (multipartRoutes.some(route => config.url?.includes(route))) {
        config.headers['Content-Type'] = 'multipart/form-data'
      } else if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json'
      }

      if (!noAuthEndpoints.some(endpoint => config.url?.includes(endpoint))) {
        // Try to get the real Supabase token first
        const token = await getCurrentAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }

      return config
    },
    error => Promise.reject(error)
  )

  apiClient.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config

      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true

        await new Promise(resolve => setTimeout(resolve, 3000))

        try {
          return await apiClient(originalRequest)
        } catch (retryError: unknown) {
          if (axios.isAxiosError(retryError) && retryError.response?.status === 403) {
            console.error("User Sign-out: API request sent 403 error twice in nest api instance", user?.email)
            window.location.href = `/logout?redirectTo=/agency/signin`
          }
          return Promise.reject(retryError)
        }
      }

      return Promise.reject(error)
    }
  )

  nestApiClients[userRegion] = apiClient
  return apiClient
}

export const getAxiosNestApiClientByUser = async (
  emailOverride?: string,
  regionOverride?: string
): Promise<AxiosInstance> => {
  if (regionOverride) {
    return await getAxiosApiClient(regionOverride)
  }

  if (!emailOverride) {
    const user = getUserFromLocalStorage()
    emailOverride = user?.userInfo.user.email
  }

  const userRegion =
    regionOverride || (await getUserDataRegion(emailOverride ?? ''))

  const apiClient = await getNestApiClient(userRegion)
  return apiClient
}

export const clearApiClients = () => {
  Object.keys(apiClients).forEach(region => delete apiClients[region])
  Object.keys(nestApiClients).forEach(region => delete nestApiClients[region])
}
